import { QueryDescriptor, QueryType } from "common/graphql/queryDescriptors";
import { StoreState } from "common/store/types";
import { useDispatch, useSelector } from "react-redux";
import { useState, useCallback, useMemo } from "react";
import { User } from "common/store/user/types";
import { useLazyQuery, useMutation, DocumentNode, MutationHookOptions, MutationTuple, QueryTuple, LazyQueryHookOptions, QueryResult, useQuery, QueryHookOptions, MutationResult } from "@apollo/client";

type MutationQueryHook = (mutation: DocumentNode, options?: MutationHookOptions<any, any>) => MutationTuple<any, any>;
type LazyQueryHook = (query: DocumentNode, options?: LazyQueryHookOptions<any, any>) => QueryTuple<any, any>;
type QueryHook = (query: DocumentNode, options?: QueryHookOptions<any, any>) => QueryResult<any, any>;

export const useCustomQuery = (
  descriptor: QueryDescriptor,
) => {
  //State
  const [errorInfo, setErrorInfo] = useState("");
  const [input, setInput] = useState(undefined);
  
  //Hooks
  const dispatch = useDispatch();
  const storageUserDetails = useSelector<StoreState, User>(
    (state) => state.currentUser
  );

  const useQueryHook: MutationQueryHook | LazyQueryHook | QueryHook = 
    descriptor.type === QueryType.Mutation ? useMutation : 
    descriptor.type === QueryType.LazyQuery ? useLazyQuery :
    useQuery;

  //Helper
  const getName = () => {
    let name = descriptor.query.definitions[0].name;
    return name ? name.value : "Unknown";
  }

  const getVariables = () => {
    return input ? input : descriptor.input ? descriptor.input(storageUserDetails) : undefined;
  }

  //Result
  let execute: any = undefined;
  let data: any = undefined;
  let loading: any = undefined;
  let error: any = undefined;

  let queryInputOptions: any = {
    variables: getVariables(),
    onError: (error: any) => {
      console.log('[' + getName() + '] - Error', {
        'input': getVariables(),
        'error': JSON.parse(JSON.stringify(error)),
      });
      
      setErrorInfo(error.message);

      if (descriptor.onError) {
        descriptor.onError(error.message, dispatch);
      }
    },
    onCompleted: (data: any) => {
      console.log('[' + getName() + '] - Success', {
        'input': getVariables(),
        'output': data,
      });

      if (descriptor.onSuccess) {
        descriptor.onSuccess(data, dispatch, storageUserDetails);
      }
    },
    pollInterval: descriptor.pollInterval,
    fetchPolicy: descriptor.fetchPolicy ? descriptor.fetchPolicy : undefined,
  };

  if (descriptor.type === QueryType.Query) {
    queryInputOptions.skip = descriptor.skip ? descriptor.skip(storageUserDetails) : undefined;
  }

  const queryHookResult = useQueryHook(
    descriptor.query,
    queryInputOptions
  );

  if (descriptor.type === QueryType.Mutation) {
    execute = queryHookResult[0];
    const mutationResult = (queryHookResult[1] as MutationResult<any>);
    data = mutationResult.data;
    loading = mutationResult.loading;
    error = mutationResult.error;
  } else if (descriptor.type === QueryType.LazyQuery) {
    execute = queryHookResult[0];
    const queryResult = (queryHookResult[1] as QueryResult<any, any>);
    data = queryResult.data;
    loading = queryResult.loading;
    error = queryResult.error;
  } else { // QueryType.Query
    const queryResult = (queryHookResult as QueryResult<any, any>);
    data = queryResult.data;
    loading = queryResult.loading;
    error = queryResult.error;
  }

  let executeMemo = useMemo(() => execute, [execute]);
  const executeWrapper = useCallback((data?: any) => {
      if (data && data.variables) {
        setInput(data.variables);
      }

      return executeMemo();
    }, [executeMemo]
  );

  return { 
    execute: executeWrapper,
    data,
    loading,
    error,
    errorInfo,
  };
}